/**
 * GlobalState.swUpdate 切片与 Service Worker 更新接线（任务 15b）。
 *
 * 判定全在 `~/sw/updateModel`（纯函数，含「安全时机」口径）；本文件只做接线：
 * - `updatefound` → 新 worker 装好且本页已被旧版接管 → ready（提示条出现）；
 * - 回前台沿：挂够久则武装自动应用 + 冷却门控内主动 `registration.update()`；
 * - 应用：`postMessage({type:'SKIP_WAITING'})`（口子在 15a 的 sw.ts）→
 *   `controllerchange` → 刷新；事件没来则 APPLY_FALLBACK_MS 后兜底直接刷。
 *
 * 接线放在 store 层而不是组件里：standalone 的自动应用必须与 UI 是否挂载无关，
 * 而且判定要读 layout.isStandalone 与 messages.timeline.outbox（都在全局 store）。
 */

import type { GlobalState, GlobalStore } from './global';
import {
  APPLY_FALLBACK_MS,
  createUpdateCheckGate,
  decideAutoApply,
  shouldArmAutoApply,
  shouldReloadOnControllerChange,
} from '~/sw/updateModel';

export interface SwUpdateSlice {
  /** 新版本已下载就绪（waiting worker 在位），刷新即生效 */
  readonly ready: boolean;
  /** 已触发应用（SKIP_WAITING 已发出，等刷新）——提示条进入「正在更新」态 */
  readonly applying: boolean;
  /** 用户关掉了提示条（本次会话内不再显示；standalone 的自动时机仍然生效） */
  readonly dismissed: boolean;
}

export const SW_UPDATE_INITIAL: SwUpdateSlice = {
  ready: false,
  applying: false,
  dismissed: false,
};

/** 提示条是否显示（applying 时继续显示，只换文案） */
export function selectUpdateBannerVisible(s: GlobalState): boolean {
  return s.swUpdate.ready && !s.swUpdate.dismissed;
}

export interface SwUpdateConnection {
  /** 用户点「立即刷新」；幂等（未就绪/已在应用中=no-op） */
  apply(): void;
  /** 用户关提示条 */
  dismiss(): void;
  dispose(): void;
}

export interface SwUpdateDeps {
  /** registerServiceWorker() 的返回；null=未注册（dev/不支持/注册失败） */
  registration: Promise<ServiceWorkerRegistration | null>;
  /**
   * SW 容器；**null = 这个浏览器根本没有 Service Worker**（微信等部分 WebView、
   * 隐私窗口、非安全上下文的 http 直连）。不是罕见情况，整条更新链必须整体 no-op，
   * 不能让 `navigator.serviceWorker.controller` 在启动时抛（19a 实测：抛了就白屏）。
   */
  container: ServiceWorkerContainer | null;
  reload(): void;
  now(): number;
  isHidden(): boolean;
  /** 事件接线目标（测试注入）；默认 document/window */
  addVisibilityListener(fn: () => void): () => void;
  addInteractionListener(fn: () => void): () => void;
  setTimer(fn: () => void, ms: number): () => void;
  /**
   * 本次加载是不是一次 reload（21ag-5 的防循环闸）。
   *
   * 冷启动自动应用会 reload，而 reload 之后又是一次「冷启动」——万一新 SW 始终
   * 接管不了（skipWaiting 没生效），没有这个闸就会变成**跨刷新的无限自刷**。
   * 判据取 `PerformanceNavigationTiming.type === 'reload'`：自刷必然是 reload，
   * 而 standalone 里用户没有手动刷新的入口，所以这个信号干净。
   */
  isReload(): boolean;
}

/** 浏览器默认依赖（main.tsx 用；node 测试里全部注入假的） */
export function browserSwUpdateDeps(
  registration: Promise<ServiceWorkerRegistration | null>,
): SwUpdateDeps {
  return {
    registration,
    container: 'serviceWorker' in navigator ? navigator.serviceWorker : null,
    reload: () => location.reload(),
    now: () => Date.now(),
    isHidden: () => document.visibilityState === 'hidden',
    addVisibilityListener: (fn) => {
      document.addEventListener('visibilitychange', fn);
      return () => document.removeEventListener('visibilitychange', fn);
    },
    isReload: () =>
      (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)
        ?.type === 'reload',
    addInteractionListener: (fn) => {
      // capture + passive：只是「人动过手」的信号，不参与任何交互语义
      const opts = { capture: true, passive: true } as const;
      window.addEventListener('pointerdown', fn, opts);
      window.addEventListener('keydown', fn, opts);
      return () => {
        window.removeEventListener('pointerdown', fn, opts);
        window.removeEventListener('keydown', fn, opts);
      };
    },
    setTimer: (fn, ms) => {
      const id = setTimeout(fn, ms);
      return () => clearTimeout(id);
    },
  };
}

function hasPendingSends(s: GlobalState): boolean {
  return (s.messages.timeline?.outbox.length ?? 0) > 0;
}

/** 没有 SW 的环境：整条链不接，提示条永不出现（ready 恒 false） */
const NOOP_CONNECTION: SwUpdateConnection = {
  apply: () => undefined,
  dismiss: () => undefined,
  dispose: () => undefined,
};

export function connectSwUpdate(store: GlobalStore, deps: SwUpdateDeps): SwUpdateConnection {
  const container = deps.container;
  if (container === null) return NOOP_CONNECTION;
  const gate = createUpdateCheckGate({ now: deps.now });
  const hadController = container.controller !== null;
  let reg: ServiceWorkerRegistration | null = null;
  /**
   * 冷启动即武装自动应用（21ag-5）。
   *
   * 原先只在「挂后台 ≥10 分钟后回前台」武装，而 standalone 用户最常见的更新
   * 时机恰恰是**关掉 Web Clip 再打开**——那是冷启动，不走那条沿，于是每次重开
   * 看到的都还是 SW 缓存里的旧外壳，必须手动点提示条才换
   * （人工报「首次安装底部正常、第二次打开就多出一块」：首次没有 SW 走网络拿到
   * 新代码，之后 SW 接管发旧缓存）。
   *
   * 冷启动其实是最安全的自动应用时机：页面刚起来，没有滚动位置、没有用户交互、
   * outbox 也是空的，刷新对用户不可见（状态由任务 13 从 IndexedDB 恢复）。
   * 其余限制（standalone / 无交互 / outbox 空 / 宽限窗口）原样沿用。
   */
  let armedAt: number | null = deps.isHidden() || deps.isReload() ? null : deps.now();
  let interactedSinceArm = false;
  let hiddenSince: number | null = deps.isHidden() ? deps.now() : null;
  let reloaded = false;
  let cancelFallback: (() => void) | null = null;
  let disposed = false;

  function reloadOnce(): void {
    if (reloaded) return;
    reloaded = true;
    cancelFallback?.();
    deps.reload();
  }

  function apply(): void {
    const slice = store.get().swUpdate;
    if (!slice.ready || slice.applying) return;
    store.set({ swUpdate: { ...slice, applying: true } });
    reg?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    // 兜底：waiting 已被别的标签页激活（postMessage 无处可去）、或 SW 没响应时，
    // 用户点了刷新就必须刷新——standalone 下这是唯一的手动出路，不能卡住。
    cancelFallback = deps.setTimer(reloadOnce, APPLY_FALLBACK_MS);
  }

  function maybeAutoApply(): void {
    if (
      decideAutoApply({
        ready: store.get().swUpdate.ready,
        applying: store.get().swUpdate.applying,
        isStandalone: store.get().layout.isStandalone,
        hasPendingSends: hasPendingSends(store.get()),
        armedAt,
        interactedSinceArm,
        now: deps.now(),
      })
    ) {
      apply();
    }
  }

  function markReady(): void {
    if (disposed) return;
    const slice = store.get().swUpdate;
    // dismissed 刻意不重置：同一个新版本用户关过就别再弹
    if (!slice.ready) store.set({ swUpdate: { ...slice, ready: true } });
    maybeAutoApply();
  }

  const offController = ((): (() => void) => {
    const onChange = (): void => {
      if (shouldReloadOnControllerChange({ hadController, applying: store.get().swUpdate.applying }))
        reloadOnce();
    };
    container.addEventListener('controllerchange', onChange);
    return () => container.removeEventListener('controllerchange', onChange);
  })();

  const offInteraction = deps.addInteractionListener(() => {
    interactedSinceArm = true;
  });

  const offVisibility = deps.addVisibilityListener(() => {
    if (deps.isHidden()) {
      hiddenSince = deps.now();
      return;
    }
    const since = hiddenSince;
    hiddenSince = null;
    if (since !== null && shouldArmAutoApply(deps.now() - since)) {
      armedAt = deps.now();
      interactedSinceArm = false;
      maybeAutoApply(); // 新版本可能早已就绪（提示条已挂着），当场应用
    }
    // 主动查新版本：standalone 可能长期不导航，浏览器自己不会查（冷却门控频率）
    if (gate.allow()) void reg?.update().catch(() => undefined);
  });

  void deps.registration.then((r) => {
    if (disposed || !r) return;
    reg = r;
    // 刷新后仍停在 waiting（上次没应用）：进来就该提示
    if (r.waiting && container.controller !== null) markReady();
    r.addEventListener('updatefound', () => {
      const installing = r.installing;
      if (!installing) return;
      const onState = (): void => {
        if (installing.state !== 'installed') return;
        installing.removeEventListener('statechange', onState);
        // 无 controller = 首次安装，不是换版（15a 的 clients.claim 会直接接管）
        if (container.controller !== null) markReady();
      };
      installing.addEventListener('statechange', onState);
    });
  });

  return {
    apply,
    dismiss() {
      const slice = store.get().swUpdate;
      if (!slice.dismissed) store.set({ swUpdate: { ...slice, dismissed: true } });
    },
    dispose() {
      disposed = true;
      cancelFallback?.();
      offController();
      offInteraction();
      offVisibility();
    },
  };
}
